import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../configuration/Request";
import ChatSidebar from "./ChatSidebar";
import ChatWindow from "./ChatWindow";

const LlmAgent = () => {
    const [conversations, setConversations] = useState({});
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [conversationNames, setConversationNames] = useState({});
    const [loading, setLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    // New states for delete confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [chatToDelete, setChatToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const token = useSelector((state) => state.counterSlice.loginDetails.jwt);
    const messagesEndRef = useRef(null);

    // Fetch chat history
    const fetchHistory = async () => {
        try {
            const res = await axiosInstance.get(
                `${process.env.REACT_APP_BASE_URL}api/llm-agent/history?limit=10`
            );

            const data = res.data.data || [];

            // Process toolMetadata to enable export buttons for history
            data.forEach((msg) => {
                if (msg.toolMetadata && !msg.exportMetadata) {
                    if (Array.isArray(msg.toolMetadata)) {
                        // Find the last tool that has filters (likely the final query)
                        for (let i = msg.toolMetadata.length - 1; i >= 0; i--) {
                            if (msg.toolMetadata[i].filters) {
                                msg.exportMetadata = msg.toolMetadata[i];
                                break; // Use the most recent one
                            }
                        }
                    } else if (msg.toolMetadata.filters) {
                        msg.exportMetadata = msg.toolMetadata;
                    }
                }
            });

            const grouped = data.reduce((acc, msg) => {
                if (!acc[msg.conversationId]) acc[msg.conversationId] = [];
                acc[msg.conversationId].push(msg);
                return acc;
            }, {});

            // Sort messages
            Object.keys(grouped).forEach((id) => {
                grouped[id].sort(
                    (a, b) =>
                        new Date(a.publishedAt || a.updatedAt || a.createdAt) -
                        new Date(b.publishedAt || b.updatedAt || b.createdAt)
                );
            });

            const names = {};
            data.forEach((msg) => {
                if (msg.title && !names[msg.conversationId]) {
                    names[msg.conversationId] = msg.title;
                }
            });

            setConversations(grouped);
            setConversationNames((prev) => ({ ...prev, ...names }));
        } catch (error) {
            console.error("Error fetching chat history:", error);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, []);

    // Helper
    const getTempTitle = (text) =>
        text ? (text.length > 30 ? text.slice(0, 30) + "..." : text) : "New Chat";

    // Send Message FIXED with Streaming
    const handleSend = async (prompt) => {
        if (!prompt) return;

        const conversationId = selectedConversation || `c-${Date.now()}`;
        const isNewChat = !selectedConversation || !conversations[conversationId] || conversations[conversationId].length === 0;

        const userMsg = {
            prompt,
            publishedAt: new Date().toISOString(),
            conversationId,
            _localId: `u-${Date.now()}`,
        };

        // Add user message instantly
        setConversations((prev) => ({
            ...prev,
            [conversationId]: [...(prev[conversationId] || []), userMsg],
        }));

        // For new chat
        if (isNewChat) {
            setConversationNames((prev) => ({
                ...prev,
                [conversationId]: getTempTitle(prompt),
            }));
            setSelectedConversation(conversationId);
        }

        const outgoingPrompt = prompt;
        setLoading(true);

        // Placeholder for AI message
        const aiMsgId = `a-${Date.now()}`;
        let currentFullResponse = "";
        let currentToolMetadata = null;
        let finalConversationId = conversationId;

        try {
            const payload = isNewChat
                ? { prompt: outgoingPrompt }
                : { conversationId, prompt: outgoingPrompt };

            const response = await fetch(`${process.env.REACT_APP_BASE_URL}api/llm-agent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            setLoading(false); // Stop loader as we start receiving stream
            setIsGenerating(true); // Start showing generating animation

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split("\n");

                for (const line of lines) {
                    if (line.startsWith("data: ")) {
                        try {
                            const data = JSON.parse(line.substring(6));

                            if (data.chunk) {
                                currentFullResponse += data.chunk;
                            }

                            if (data.toolMetadata) {
                                currentToolMetadata = data.toolMetadata;
                            }

                            if (data.conversationId) {
                                finalConversationId = data.conversationId;
                            }

                            if (data.done) {
                                // Remove "Searching database..." message when done
                                currentFullResponse = currentFullResponse.replace(/\n\n\*Searching database\.\.\.\*\n\n/g, "");
                                // Also handle cases where it might be slightly different or at the end
                                currentFullResponse = currentFullResponse.replace(/\*Searching database\.\.\.\*/g, "");
                                setIsGenerating(false); // Stop showing generating animation
                            }

                            const aiMsg = {
                                response: currentFullResponse,
                                updatedAt: new Date().toISOString(),
                                conversationId: finalConversationId,
                                _localId: aiMsgId,
                                exportMetadata: currentToolMetadata,
                            };

                            // Update AI message in real-time
                            setConversations((prev) => {
                                const newConversations = { ...prev };

                                if (isNewChat && finalConversationId !== conversationId && !newConversations[finalConversationId]) {
                                    const tempMessages = prev[conversationId] || [];
                                    const filteredMessages = tempMessages.filter(m => m._localId !== aiMsgId);

                                    if (newConversations[conversationId]) {
                                        delete newConversations[conversationId];
                                    }
                                    newConversations[finalConversationId] = [...filteredMessages, aiMsg];

                                    if (selectedConversation !== finalConversationId) {
                                        setSelectedConversation(finalConversationId);
                                        setConversationNames((prevNames) => {
                                            const newNames = { ...prevNames };
                                            if (newNames[conversationId]) {
                                                newNames[finalConversationId] = data.title || newNames[conversationId];
                                                delete newNames[conversationId];
                                            }
                                            return newNames;
                                        });
                                    }
                                } else {
                                    const targetId = finalConversationId;
                                    const msgs = [...(newConversations[targetId] || [])];
                                    const existingIdx = msgs.findIndex(m => m._localId === aiMsgId);
                                    if (existingIdx > -1) {
                                        msgs[existingIdx] = aiMsg;
                                    } else {
                                        msgs.push(aiMsg);
                                    }
                                    newConversations[targetId] = msgs;

                                    if (isNewChat && targetId !== conversationId && newConversations[conversationId]) {
                                        delete newConversations[conversationId];
                                    }
                                }

                                return newConversations;
                            });

                        } catch (e) {
                            console.error("Error parsing SSE chunk", e);
                        }
                    }
                }
            }

        } catch (error) {
            console.error("Error sending message:", error);
            setLoading(false);
            setIsGenerating(false); // Stop animation on error

            const errMsg = {
                response: "Failed to send message. Please try again.",
                updatedAt: new Date().toISOString(),
                conversationId: finalConversationId,
                _localId: `err-${Date.now()}`,
            };

            setConversations((prev) => ({
                ...prev,
                [finalConversationId]: [...(prev[finalConversationId] || []), errMsg],
            }));
        }
    };

    // New Chat
    const handleNewChat = () => {
        // Just clear the selection to show the welcome screen
        setSelectedConversation(null);
    };

    // Delete Chat
    const handleDeleteChat = async () => {
        if (!chatToDelete) return;

        setIsDeleting(true);
        try {
            await axiosInstance.delete(
                `${process.env.REACT_APP_BASE_URL}api/llm-agent?conversationId=${chatToDelete}`
            );

            // Update state
            setConversations((prev) => {
                const updated = { ...prev };
                delete updated[chatToDelete];
                return updated;
            });
            setConversationNames((prev) => {
                const updated = { ...prev };
                delete updated[chatToDelete];
                return updated;
            });

            if (selectedConversation === chatToDelete) {
                setSelectedConversation(null);
            }

            setShowDeleteConfirm(false);
            setChatToDelete(null);
        } catch (error) {
            console.error("Error deleting chat:", error);
            alert("Failed to delete chat. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const confirmDelete = (e, id) => {
        e.stopPropagation();
        setChatToDelete(id);
        setShowDeleteConfirm(true);
    };

    // Export to PDF
    const handleExportPdf = async () => {
        if (!selectedConversation) return;

        setIsExporting(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}api/llm-agent/download/pdf/${selectedConversation}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) throw new Error("Failed to download PDF");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Chat-${selectedConversation}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Error exporting PDF:", error);
            alert("Failed to export chat as PDF.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="chat-container">
            <ChatSidebar
                conversations={Object.keys(conversations).reverse()}
                activeChat={selectedConversation}
                onSelectChat={setSelectedConversation}
                onNewChat={handleNewChat}
                onDeleteChat={confirmDelete}
                conversationNames={conversationNames}
                isGenerating={isGenerating}
            />

            <ChatWindow
                activeChat={selectedConversation}
                conversationName={conversationNames[selectedConversation]}
                messages={conversations[selectedConversation] || []}
                onSend={handleSend}
                loading={loading}
                isGenerating={isGenerating}
                isExporting={isExporting}
                onExportPdf={handleExportPdf}
                token={token}
                baseUrl={process.env.REACT_APP_BASE_URL}
            />

            {/* Custom Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="custom-modal">
                        <h3>Delete Chat?</h3>
                        <p>Are you sure you want to delete this conversation? This action cannot be undone.</p>
                        <div className="modal-actions">
                            <button
                                className="cancel-btn"
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-delete-btn"
                                onClick={handleDeleteChat}
                                disabled={isDeleting}
                            >
                                {isDeleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LlmAgent;